/*
 * Пользовательские настройки приложения.
 *
 * Тема, размер текста и язык остаются настройками устройства. Уведомления
 * синхронизируются с API, а localStorage служит их офлайн-кэшем.
 */

import { DEFAULT_THEME_ID, themeById, themeVariables } from '@andresot/tokens/themes';
import type { NotificationSettings } from '@andresot/protocol';
import * as notificationApi from '$api/notifications';

const KEY = 'andresot:prefs';
const NOTIFICATION_KEYS = [
	'notifyMessages',
	'notifyCalls',
	'notifyVideos',
	'notifyDating',
	'notifySound',
	'notifyPreview',
	'quietHours',
	'quietStartMinutes',
	'quietEndMinutes',
	'timezoneOffsetMinutes'
] as const;
const SERVER_KEYS = {
	notifyMessages: 'messages',
	notifyCalls: 'calls',
	notifyVideos: 'videos',
	notifyDating: 'dating',
	notifySound: 'sound',
	notifyPreview: 'preview',
	quietHours: 'quietHours'
} as const;
type NotificationKey = keyof typeof SERVER_KEYS;

export type ThemeChoice = 'dark' | 'light' | 'system';

interface StoredPrefs {
	theme: ThemeChoice;
	/** Выбранная цветная тема оформления. */
	colorTheme: string;
	textSize: number;
	chatPattern: boolean;
	compactList: boolean;
	/** Отправлять фотографии как есть, без уменьшения перед отправкой. */
	originalQuality: boolean;
	language: string;
	notifyMessages: boolean;
	notifyCalls: boolean;
	notifyVideos: boolean;
	notifyDating: boolean;
	notifySound: boolean;
	notifyPreview: boolean;
	quietHours: boolean;
	quietStartMinutes: number;
	quietEndMinutes: number;
	timezoneOffsetMinutes: number;
}

const DEFAULTS: StoredPrefs = {
	theme: 'dark',
	colorTheme: DEFAULT_THEME_ID,
	textSize: 16,
	chatPattern: true,
	compactList: false,
	/*
	 * По умолчанию фотографии сжимаются. Так делают все мессенджеры, и
	 * причина не в экономии места на сервере: снимок с телефона уходит на
	 * слабой связи минутами, а в пузыре его всё равно не разглядеть.
	 */
	originalQuality: false,
	language: 'ru',
	notifyMessages: true,
	notifyCalls: true,
	notifyVideos: true,
	notifyDating: true,
	notifySound: true,
	notifyPreview: true,
	quietHours: false,
	quietStartMinutes: 23 * 60,
	quietEndMinutes: 8 * 60,
	timezoneOffsetMinutes: new Date().getTimezoneOffset()
};

class PrefsState {
	theme = $state<ThemeChoice>(DEFAULTS.theme);
	colorTheme = $state(DEFAULTS.colorTheme);
	textSize = $state(DEFAULTS.textSize);
	chatPattern = $state(DEFAULTS.chatPattern);
	compactList = $state(DEFAULTS.compactList);
	originalQuality = $state(DEFAULTS.originalQuality);
	language = $state(DEFAULTS.language);
	notifyMessages = $state(DEFAULTS.notifyMessages);
	notifyCalls = $state(DEFAULTS.notifyCalls);
	notifyVideos = $state(DEFAULTS.notifyVideos);
	notifyDating = $state(DEFAULTS.notifyDating);
	notifySound = $state(DEFAULTS.notifySound);
	notifyPreview = $state(DEFAULTS.notifyPreview);
	quietHours = $state(DEFAULTS.quietHours);
	quietStartMinutes = $state(DEFAULTS.quietStartMinutes);
	quietEndMinutes = $state(DEFAULTS.quietEndMinutes);
	timezoneOffsetMinutes = $state(DEFAULTS.timezoneOffsetMinutes);
	notificationsLoading = $state(false);
	notificationsLoaded = $state(false);
	private notificationOwner: string | null = null;
	private lifecycle = 0;
	private loadRequest = 0;
	private serverRevision = 0;
	private mutation = 0;
	private queue: Promise<unknown> = Promise.resolve();
	private pending = new Map<NotificationKey, { id: number; value: boolean }>();
	private confirmed: NotificationSettings = this.notificationSnapshot();

	constructor() {
		const stored = load();
		// Старый общий кеш уведомлений не имеет владельца: переносить его в
		// произвольный аккаунт нельзя. Оформление остаётся настройкой устройства.
		for (const key of Object.keys(DEFAULTS) as (keyof StoredPrefs)[]) {
			if (!(NOTIFICATION_KEYS as readonly string[]).includes(key) && stored[key] !== undefined) {
				Object.assign(this, { [key]: stored[key] });
			}
		}
	}

	beginNotifications(userId: string | null): void {
		this.lifecycle += 1;
		this.notificationOwner = userId;
		this.loadRequest += 1;
		this.serverRevision = 0;
		this.queue = Promise.resolve();
		this.pending.clear();
		this.notificationsLoading = false;
		this.notificationsLoaded = false;
		let stored: Partial<StoredPrefs> = {};
		try {
			if (userId) {
				const value = JSON.parse(localStorage.getItem(`${KEY}:notifications:${userId}`) ?? '{}');
				if (value && typeof value === 'object') stored = value;
			}
		} catch {
			/* Кеш может быть недоступен. */
		}
		for (const key of NOTIFICATION_KEYS) {
			const value = stored[key];
			Object.assign(this, { [key]: typeof value === typeof DEFAULTS[key] ? value : DEFAULTS[key] });
		}
		this.confirmed = this.notificationSnapshot();
	}

	private notificationSnapshot(): NotificationSettings {
		return {
			messages: this.notifyMessages,
			calls: this.notifyCalls,
			videos: this.notifyVideos,
			dating: this.notifyDating,
			sound: this.notifySound,
			preview: this.notifyPreview,
			quietHours: this.quietHours,
			quietStartMinutes: this.quietStartMinutes,
			quietEndMinutes: this.quietEndMinutes,
			timezoneOffsetMinutes: this.timezoneOffsetMinutes
		};
	}

	private enqueue<T>(lifecycle: number, task: () => Promise<T>): Promise<T | undefined> {
		const run = this.queue.then(async () => {
			if (lifecycle !== this.lifecycle) return undefined;
			try {
				return await task();
			} catch (error) {
				if (lifecycle === this.lifecycle) throw error;
				return undefined;
			}
		});
		this.queue = run.catch(() => undefined);
		return run;
	}

	setTheme(theme: ThemeChoice): void {
		this.theme = theme;
		applyTheme(theme);
		// Цветная тема зависит от световой схемы: у светлой свои оттенки
		// пузырей и пятен, поэтому её нужно пересчитать.
		applyColorTheme(this.colorTheme, theme);
		this.save();
	}

	setColorTheme(id: string): void {
		this.colorTheme = id;
		applyColorTheme(id, this.theme);
		this.save();
	}

	setTextSize(size: number): void {
		this.textSize = size;
		applyTextSize(size);
		this.save();
	}

	setLanguage(code: string): void {
		this.language = code;
		this.save();
	}

	/** Загрузить общие настройки после входа; локальная копия служит офлайн-кэшем. */
	async loadNotifications(): Promise<void> {
		if (!this.notificationOwner) return;
		const lifecycle = this.lifecycle;
		const request = ++this.loadRequest;
		const revision = this.serverRevision;
		const current = () => lifecycle === this.lifecycle && request === this.loadRequest;
		this.notificationsLoading = true;
		try {
			let settings = await notificationApi.fetchNotificationSettings();
			if (!current() || revision !== this.serverRevision) return;
			const timezoneOffsetMinutes = new Date().getTimezoneOffset();
			if (settings.timezoneOffsetMinutes !== timezoneOffsetMinutes) {
				const updated = await this.enqueue(lifecycle, async () => {
					if (!current()) return undefined;
					return notificationApi.updateNotificationSettings({ timezoneOffsetMinutes });
				});
				if (!current() || !updated) return;
				settings = updated;
				this.serverRevision += 1;
			}
			if (!current()) return;
			this.confirmed = settings;
			this.applyNotifications(settings);
			this.notificationsLoaded = true;
		} catch (error) {
			if (current()) throw error;
		} finally {
			if (current()) this.notificationsLoading = false;
		}
	}

	async setNotification(key: NotificationKey, value: boolean): Promise<void> {
		if (!this.notificationOwner) return;
		const lifecycle = this.lifecycle;
		const id = ++this.mutation;
		this.pending.set(key, { id, value });
		this[key] = value;
		this.save();

		try {
			const settings = await this.enqueue(lifecycle, () =>
				notificationApi.updateNotificationSettings({ [SERVER_KEYS[key]]: value })
			);
			if (lifecycle !== this.lifecycle || !settings) return;
			this.serverRevision += 1;
			this.confirmed = settings;
			if (this.pending.get(key)?.id === id) this.pending.delete(key);
			this.applyNotifications(settings);
			this.notificationsLoaded = true;
		} catch (error) {
			if (lifecycle !== this.lifecycle || this.pending.get(key)?.id !== id) return;
			this.pending.delete(key);
			this.applyNotifications(this.confirmed);
			throw error;
		}
	}

	private applyNotifications(settings: NotificationSettings): void {
		this.notifyMessages = settings.messages;
		this.notifyCalls = settings.calls;
		this.notifyVideos = settings.videos;
		this.notifyDating = settings.dating;
		this.notifySound = settings.sound;
		this.notifyPreview = settings.preview;
		this.quietHours = settings.quietHours;
		this.quietStartMinutes = settings.quietStartMinutes;
		this.quietEndMinutes = settings.quietEndMinutes;
		this.timezoneOffsetMinutes = settings.timezoneOffsetMinutes;
		for (const [key, change] of this.pending) this[key] = change.value;
		this.save();
	}

	shouldPlayNotificationSound(kind: 'messages' | 'calls', now = new Date()): boolean {
		if ((kind === 'messages' ? !this.notifyMessages : !this.notifyCalls) || !this.notifySound) {
			return false;
		}
		return !this.isQuietNow(now);
	}

	isQuietNow(now = new Date()): boolean {
		if (!this.quietHours) return false;
		const utcMinutes = now.getUTCHours() * 60 + now.getUTCMinutes();
		const localMinutes = (utcMinutes - this.timezoneOffsetMinutes + 24 * 60) % (24 * 60);
		const start = this.quietStartMinutes;
		const end = this.quietEndMinutes;
		if (start === end) return true;
		const quiet =
			start < end
				? localMinutes >= start && localMinutes < end
				: localMinutes >= start || localMinutes < end;
		return quiet;
	}

	shouldRingNow(now = new Date()): boolean {
		return this.shouldPlayNotificationSound('calls', now);
	}

	/** Применить всё сохранённое к странице — вызывается один раз при запуске. */
	apply(): void {
		applyTheme(this.theme);
		applyColorTheme(this.colorTheme, this.theme);
		applyTextSize(this.textSize);
	}

	save(): void {
		try {
			const snapshot: StoredPrefs = {
				theme: this.theme,
				colorTheme: this.colorTheme,
				textSize: this.textSize,
				chatPattern: this.chatPattern,
				compactList: this.compactList,
				originalQuality: this.originalQuality,
				language: this.language,
				notifyMessages: this.notifyMessages,
				notifyCalls: this.notifyCalls,
				notifyVideos: this.notifyVideos,
				notifyDating: this.notifyDating,
				notifySound: this.notifySound,
				notifyPreview: this.notifyPreview,
				quietHours: this.quietHours,
				quietStartMinutes: this.quietStartMinutes,
				quietEndMinutes: this.quietEndMinutes,
				timezoneOffsetMinutes: this.timezoneOffsetMinutes
			};

			const device = { ...snapshot } as Partial<StoredPrefs>;
			const notifications: Partial<StoredPrefs> = {};
			for (const key of NOTIFICATION_KEYS) {
				Object.assign(notifications, { [key]: snapshot[key] });
				delete device[key];
			}
			localStorage.setItem(KEY, JSON.stringify(device));
			if (this.notificationOwner)
				localStorage.setItem(
					`${KEY}:notifications:${this.notificationOwner}`,
					JSON.stringify(notifications)
				);
		} catch {
			// Приватный режим — настройки не переживут перезагрузку.
		}
	}
}

/**
 * Тема применяется атрибутом на <html>. Значение `system` атрибут снимает —
 * тогда вступает в силу правило @media (prefers-color-scheme) из токенов,
 * и приложение следует за настройкой Windows.
 */
function applyTheme(theme: ThemeChoice): void {
	if (typeof document === 'undefined') return;

	if (theme === 'system') {
		delete document.documentElement.dataset.theme;
	} else {
		document.documentElement.dataset.theme = theme;
	}

	/* Android берёт фон области системного жеста из theme-color. Значение
	 * должно следовать теме самого приложения, а не теме телефона: иначе у
	 * тёмного интерфейса появлялась белая полоса снизу. */
	const resolvedTheme =
		theme === 'system'
			? window.matchMedia('(prefers-color-scheme: light)').matches
				? 'light'
				: 'dark'
			: theme;
	const themeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
	themeColor?.setAttribute('content', resolvedTheme === 'light' ? '#ffffff' : '#141214');
}

/**
 * Цветная тема выставляет переменные прямо в стиле корневого элемента.
 * Значения из стиля элемента перекрывают объявления из таблицы стилей,
 * поэтому переопределять правила `colors.css` не требуется — тем же приёмом
 * работает размер текста.
 */
function applyColorTheme(id: string, choice: ThemeChoice): void {
	if (typeof document === 'undefined') return;

	// При системной схеме спрашиваем настройку операционной системы:
	// у светлой и тёмной у темы разные оттенки пузырей и пятен.
	const scheme: 'dark' | 'light' =
		choice === 'system'
			? window.matchMedia('(prefers-color-scheme: light)').matches
				? 'light'
				: 'dark'
			: choice;

	const variables = themeVariables(themeById(id), scheme);

	for (const [name, value] of Object.entries(variables)) {
		document.documentElement.style.setProperty(name, value);
	}
}

/**
 * Размер текста — один множитель на всё содержимое.
 *
 * Раньше здесь переписывались две переменные, и менялся только текст
 * сообщений: списки чатов, вторые строки, подписи и кнопки набраны другими
 * размерами и оставались прежними. Теперь настройка ставит `--font-scale`, а
 * размеры считаются от него в самих токенах — одно место вместо девяти.
 *
 * Шестнадцать — обычный размер, от него и считается доля.
 */
function applyTextSize(size: number): void {
	if (typeof document === 'undefined') return;

	document.documentElement.style.setProperty('--font-scale', String(size / 16));
}

function load(): Partial<StoredPrefs> {
	try {
		const raw = localStorage.getItem(KEY);
		return raw ? (JSON.parse(raw) as Partial<StoredPrefs>) : {};
	} catch {
		return {};
	}
}

export const prefs = new PrefsState();
